import {
  getFriendlyLabel,
  getPropKey,
  VariablePill,
  variablePillAccentClassName,
  variablePillClassName,
} from "@/components/ui/workflow-step-row/components/VariablePill";
import { cn } from "@/lib/utils";
import type { ChainInfo } from "../_hooks/useChainInfo";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { isAddress } from "viem";

const REMARK_PLUGINS = [remarkGfm];

/**
 * Calculate duration between two timestamps
 */
export const calculateDuration = (start: string | undefined, end: string | undefined): string => {
  if (!start || !end) return "";
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate.getTime() - startDate.getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  } catch {
    return "";
  }
};

/** Cap native tooltip text to avoid dumping huge code blocks */
export const tooltipText = (text: string, max = 100): string =>
  text.length > max ? text.slice(0, max) + "\u2026" : text;

export type { ChainInfo };

export interface TokenInfo {
  chainId: number;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  metadata: {
    logoURI?: string;
    verified: boolean;
    isNative: boolean;
  };
}

// Helper to format values for display
export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const isFullEthereumAddress = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("0x") && value.length === 42 && isAddress(value);

export const isHexLikeValue = (value: unknown): value is string =>
  typeof value === "string" && /^0x[a-fA-F0-9]+$/.test(value);

export const truncateHexValue = (value: string, start = 6, end = 4): string => {
  if (!value.startsWith("0x") || value.length <= start + end + 3) return value;
  return `${value.slice(0, start)}...${value.slice(-end)}`;
};

/**
 * Renders a string with {{variables}} highlighted in blue pills
 * Strips the {{ }} braces for cleaner display
 * Used for template and message fields in action nodes
 */
export const renderWithVariableHighlights = (
  text: string,
  getNodeName?: (nodeId: string) => string | undefined,
): React.ReactNode => {
  if (!text || typeof text !== "string") return text;

  // Match all variable patterns like {{anything}} or @{{anything}}
  const variableRegex = /(@?\{\{[^}]+\}\})/g;
  const parts = text.split(variableRegex);

  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    if (part.match(variableRegex)) {
      // Strip the braces and optionally the @ prefix for display
      const hasAtPrefix = part.startsWith("@{{");
      const innerPath = hasAtPrefix ? part.slice(3, -2) : part.slice(2, -2);

      // For $props variables, use VariablePill which can resolve values via context
      if (getPropKey(innerPath) !== null) {
        return React.createElement(VariablePill, { key: index, variable: part });
      }

      // Check for friendly label first (@ and $ variables)
      const friendly = getFriendlyLabel(innerPath);
      if (friendly) {
        return React.createElement(
          "span",
          {
            key: index,
            className: variablePillClassName,
            title: innerPath,
          },
          React.createElement("span", { className: "truncate" }, friendly),
        );
      }

      const pathParts = innerPath.split(".");

      // Build display: "NodeName: field" or just "field" if no node lookup
      // e.g., "trigger-abc.result.price" -> "CoinGecko: price"
      let nodeName: string | undefined;
      let fieldPath: string;
      if (
        pathParts.length > 2 &&
        (pathParts[1] === "result" || pathParts[1] === "payload" || pathParts[1] === "input")
      ) {
        fieldPath = pathParts.slice(2).join(".");
        nodeName = getNodeName?.(pathParts[0]);
      } else {
        fieldPath = innerPath;
      }

      return React.createElement(
        "span",
        {
          key: index,
          className: variablePillClassName,
          title: innerPath,
        },
        hasAtPrefix && React.createElement("span", { className: variablePillAccentClassName }, "@"),
        nodeName &&
          React.createElement("span", { className: cn("truncate", variablePillAccentClassName) }, nodeName + ":"),
        React.createElement("span", { className: "truncate" }, fieldPath),
      );
    }
    return part;
  });
};

/**
 * Truncates text to first N lines
 */
export const truncateToLines = (text: string, maxLines: number): { truncated: string; wasTruncated: boolean } => {
  const lines = text.split("\n");
  if (lines.length <= maxLines) {
    return { truncated: text, wasTruncated: false };
  }
  return { truncated: lines.slice(0, maxLines).join("\n"), wasTruncated: true };
};

/**
 * Process React children to highlight {{variable}} patterns
 */
export const processChildrenForVariables = (
  children: React.ReactNode,
  getNodeName?: (nodeId: string) => string | undefined,
): React.ReactNode => {
  if (typeof children === "string") {
    return renderWithVariableHighlights(children, getNodeName);
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (typeof child === "string") {
        return React.createElement("span", { key: i }, renderWithVariableHighlights(child, getNodeName));
      }
      return child;
    });
  }
  return children;
};

/**
 * Compact markdown renderer for templates that highlights {{variables}}
 * Shows first 4 lines with truncation indicator
 * Converts single newlines to line breaks (like GitHub-flavored markdown)
 */
export const TemplateMarkdownPreview = ({
  content,
  getNodeName,
}: {
  content: string;
  getNodeName?: (nodeId: string) => string | undefined;
}) => {
  const { truncated, wasTruncated } = truncateToLines(content, 4);

  // Convert single newlines to double newlines so markdown treats them as line breaks
  // But preserve double newlines (paragraph breaks) as-is
  const processedContent = truncated.replace(/(?<!\n)\n(?!\n)/g, "  \n");

  return React.createElement(
    "div",
    { className: "template-preview text-xs text-ds-text-secondary" },
    React.createElement(
      ReactMarkdown,
      {
        remarkPlugins: REMARK_PLUGINS,
        components: {
          // Override text nodes to highlight variables
          p: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "p",
              { className: "mb-1 leading-relaxed last:mb-0" },
              processChildrenForVariables(children, getNodeName),
            ),
          strong: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "strong",
              { className: "font-semibold" },
              processChildrenForVariables(children, getNodeName),
            ),
          em: ({ children }: { children?: React.ReactNode }) =>
            React.createElement("em", { className: "italic" }, processChildrenForVariables(children, getNodeName)),
          li: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "li",
              { className: "leading-relaxed" },
              processChildrenForVariables(children, getNodeName),
            ),
          ul: ({ children }: { children?: React.ReactNode }) =>
            React.createElement("ul", { className: "list-disc space-y-0.5 pl-4" }, children),
          ol: ({ children }: { children?: React.ReactNode }) =>
            React.createElement("ol", { className: "list-decimal space-y-0.5 pl-4" }, children),
          a: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "span",
              { className: "text-blue-600 underline dark:text-blue-400" },
              processChildrenForVariables(children, getNodeName),
            ),
          code: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "code",
              { className: "rounded bg-ds-neutral-100 px-1 py-0.5 font-mono text-3xs dark:bg-ds-neutral-800" },
              processChildrenForVariables(children, getNodeName),
            ),
          h1: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "div",
              { className: "mb-1 font-bold" },
              processChildrenForVariables(children, getNodeName),
            ),
          h2: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "div",
              { className: "mb-1 font-bold" },
              processChildrenForVariables(children, getNodeName),
            ),
          h3: ({ children }: { children?: React.ReactNode }) =>
            React.createElement(
              "div",
              { className: "mb-0.5 font-semibold" },
              processChildrenForVariables(children, getNodeName),
            ),
        },
      },
      processedContent,
    ),
    wasTruncated && React.createElement("span", { className: "text-ds-text-disabled dark:text-ds-text-muted" }, "..."),
  );
};

/**
 * Extracts template-local variable names from a format action's data field
 * Returns array of variable names (e.g., ["@price", "@triggeredAt"])
 */
export const extractTemplateVariableNames = (dataValue: unknown): string[] => {
  const variables: string[] = [];

  let dataObj: Record<string, unknown> | null = null;

  if (typeof dataValue === "object" && dataValue !== null && !Array.isArray(dataValue)) {
    dataObj = dataValue as Record<string, unknown>;
  } else if (typeof dataValue === "string") {
    const trimmed = dataValue.trim();
    if (trimmed.startsWith("{") && !trimmed.startsWith("{{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          dataObj = parsed as Record<string, unknown>;
        }
      } catch {
        // Not valid JSON
      }
    }
  }

  if (dataObj) {
    Object.keys(dataObj).forEach(key => {
      variables.push(`@${key}`);
    });
  }

  return variables;
};

/**
 * Converts milliseconds to a human-readable time format
 * Returns seconds if < 60s, minutes if < 60min, hours otherwise
 */
export const formatDelayTime = (ms: number): string => {
  const seconds = ms / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;

  if (seconds < 60) {
    return `${seconds}s`;
  } else if (minutes < 60) {
    return `${minutes.toFixed(minutes % 1 === 0 ? 0 : 1)}m`;
  } else {
    return `${hours.toFixed(hours % 1 === 0 ? 0 : 1)}h`;
  }
};

/**
 * Helper function to detect if payload is a math operation
 */
export const isMathOperationPayload = (payload?: Record<string, unknown>): boolean => {
  if (!payload) return false;
  const keys = Object.keys(payload).map(k => k.toLowerCase());
  return keys.includes("left") && keys.includes("operator") && keys.includes("right");
};

/**
 * Helper function to format math operation display
 */
export const formatMathOperation = (left: unknown, operator: unknown, right: unknown, rounding: unknown): string => {
  let operation = `${left} ${operator} ${right}`;

  if (rounding && rounding !== "none") {
    operation = `${rounding}(${operation})`;
  }

  return operation;
};

/**
 * Format URL for display: shows hostname + pathname without protocol
 */
export const formatDisplayUrl = (url: string | undefined): string => {
  if (!url) return "";
  try {
    const urlObj = new URL(url);
    return urlObj.hostname + urlObj.pathname;
  } catch {
    return url;
  }
};

/**
 * Check if a string value is a valid hex color
 * Supports #RGB, #RRGGBB, and #RRGGBBAA formats
 */
export const isHexColor = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(value);
};
